import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState
} from "react";

import { useSelector } from "react-redux";
import { useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";
import { thunkFetchAdmins } from "../../store/users/actions";
import {
  Checkbox,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { AppState } from "../../store";
import { Person } from "../../models/Person";

interface PersonMultiSelectProps extends Omit<SelectProps, "loading"> {
  includeExternal: boolean;
  label?: string;
  onSelected?: (value: ReadonlyArray<Person>) => void;
  variant?: "filled" | "outlined" | "standard";
  value?: ReadonlyArray<string>;
  hide?: ReadonlyArray<any>;
}

export const PersonMultiSelect: FunctionComponent<PersonMultiSelectProps> = ({
  includeExternal,
  label = "Person",
  onSelected,
  onChange,
  variant = "standard",
  value = [],
  hide = [],
  ...props
}) => {
  const admins =
    useSelector<AppState, ReadonlyArray<Person>>(s => s.users.admins) || [];

  const [state, setState] = useState<ReadonlyArray<Person>>([]);

  useLoadIfEmpty(!admins.length, thunkFetchAdmins);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let ids = event.target.value as Array<string>;
      let values = state.filter(x => ids.includes(x.id));

      onSelected(values ?? []);
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderNames = (ids: ReadonlyArray<string>): ReactNode => {
    return state
      .filter(x => ids.includes(x.id))
      .map(x => x.name)
      .join(", ");
  };

  useEffect(() => {
    let filtered =
      admins &&
      admins.length &&
      admins
        .filter(x => includeExternal || x.type === "LifvsPersonnel")
        .filter(x => !hide.some(y => y.id === x.id))
        .sort((a, b) => a.name.localeCompare(b.name));

    if (filtered) {
      setState(filtered);
    }
  }, [admins]);

  return (
    <FormControl variant={variant} className={props.className}>
      <InputLabel
        id="priorities-multi-select-label"
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {label}
      </InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        labelId="person-multi-select-label"
        id="person-multi-select"
        autoWidth={true}
        input={<Input />}
        renderValue={value => renderNames(value as string[])}
      >
        {state &&
          state.length > 0 &&
          state.map(x => (
            <MenuItem key={x.id} value={x.id}>
              <Checkbox checked={value.indexOf(x.id) > -1} />
              <ListItemText primary={x.name} />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
